// One reader for the whole v2 shell: request dedup and a short cache.
//
// THE PROBLEM, MEASURED
// =====================
// Business-unit health is fetched by six components independently. Scheduler
// health by six. Integration health by four. Each one mounts, each one issues
// its own GET, and the backend answers the same question sixteen times for a
// single page load. Nothing is shared because nothing was ever asked to share —
// the shell grew three separate readers, one per area:
//
//   routes/operations/useEndpoint.js   axios, statuses idle/loading/ready/refused/failed
//   routes/settings/useResource.js     statuses loading/ok/empty/denied/disabled/error
//   routes/workstation/api.js          { ok, data, error }, withCredentials
//
// Three vocabularies for the same four outcomes, and sixteen tabs still to
// build. This module is the one they should all become.
//
// DELIBERATELY NOT CLEVER
// =======================
// No stale-while-revalidate, no background refresh, no query invalidation graph,
// no subscription model. Two mechanisms only:
//
//   dedup   — concurrent callers asking for the same key share one in-flight
//             promise. This is what turns six requests into one.
//   cache   — a short TTL so a remount inside the same interaction does not
//             re-ask. Short, because the alternative is a surface showing
//             yesterday's number and looking correct while doing it.
//
// Every tab that gets built inherits whatever is here, so the cost of a clever
// mechanism is paid sixteen times and the cost of a wrong one is paid in numbers
// that are quietly out of date. Simple is the whole design.
//
// CREDENTIALS ARE ALWAYS SENT
// ===========================
// `workstation/api.js` sets `withCredentials`; `operations/useEndpoint.js` does
// not. Production auth is session-based, so the correct behaviour is to send
// them, and this layer does that uniformly rather than inheriting the
// inconsistency. Noted because it is a real behaviour change for the Operations
// surfaces at the point they migrate, not something to discover then.
import axios from "axios";

import { FAILED, OFFLINE, READY, REFUSED } from "../states";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** How long a successful read stays warm. Short on purpose. */
export const DEFAULT_TTL_MS = 15000;

/** key -> { at, outcome } */
const cache = new Map();
/** key -> Promise<outcome>, for the requests currently in the air. */
const inflight = new Map();

/**
 * The cache key. Params are sorted so that `{a,b}` and `{b,a}` are one entry
 * rather than two — a caller building params in a different order must not
 * silently double the traffic.
 */
export function cacheKey(path, params) {
  if (!params) return path;
  const sorted = Object.keys(params)
    .filter((k) => params[k] !== undefined)
    .sort()
    .map((k) => `${k}=${JSON.stringify(params[k])}`)
    .join("&");
  return sorted ? `${path}?${sorted}` : path;
}

/**
 * Map a transport result onto the §11.4 vocabulary.
 *
 * The distinction that matters: 401/403 is REFUSED, not FAILED. The server
 * enforcing a gate is not this surface breaking, and rendering it as a failure
 * teaches the operator to ignore real failures. No response at all is OFFLINE,
 * which is a third thing again — "we could not ask" rather than "we asked and it
 * went wrong".
 */
function outcomeFor(err) {
  const code = err?.response?.status;
  if (code === 401 || code === 403) {
    return { status: REFUSED, data: null, error: `HTTP ${code}`, httpStatus: code };
  }
  if (!err?.response) {
    return { status: OFFLINE, data: null, error: "unreachable", httpStatus: null };
  }
  return { status: FAILED, data: null, error: `HTTP ${code}`, httpStatus: code };
}

/**
 * Read one endpoint.
 *
 * Resolves to `{ status, data, error, httpStatus }` and never rejects — a caller
 * rendering a state must not also need a try/catch.
 *
 * `ttl: 0` forces a fresh read and still dedups against anything already in the
 * air, which is what a "refresh" button wants.
 */
export function read(path, { params, ttl = DEFAULT_TTL_MS, signal } = {}) {
  const key = cacheKey(path, params);

  if (ttl > 0) {
    const hit = cache.get(key);
    if (hit && Date.now() - hit.at < ttl) {
      return Promise.resolve(hit.outcome);
    }
  }

  // The dedup itself. Six mounting components produce one request because the
  // second through sixth find this entry and await the same promise.
  const pending = inflight.get(key);
  if (pending) return pending;

  const req = axios
    .get(`${API}${path}`, { params, withCredentials: true, signal })
    .then((res) => ({
      status: READY,
      data: res.data,
      error: null,
      httpStatus: res.status,
    }))
    .catch(outcomeFor)
    .then((outcome) => {
      // Only successful reads are cached. Caching a refusal or a failure would
      // make a transient outage sticky for the TTL, and make a permissions
      // change take fifteen seconds to become visible.
      if (outcome.status === READY) cache.set(key, { at: Date.now(), outcome });
      inflight.delete(key);
      return outcome;
    });

  inflight.set(key, req);
  return req;
}

/**
 * Drop cached reads. No argument clears everything; a string clears every key
 * for that path prefix.
 *
 * In-flight requests are deliberately left alone — cancelling them would abort
 * a read some other component is still waiting on.
 */
export function invalidate(path) {
  if (!path) {
    cache.clear();
    return;
  }
  for (const key of cache.keys()) {
    if (key === path || key.startsWith(`${path}?`)) cache.delete(key);
  }
}

/** Test seam. Not for application code. */
export function __reset() {
  cache.clear();
  inflight.clear();
}

/** Test seam: what the layer is currently holding. */
export function __stats() {
  return { cached: cache.size, inflight: inflight.size };
}
